/**
 * Real-time posted order updates via WebSocket.
 * Listens for `posted_order_update` messages and invokes onPostedOrderEvent.
 * Same WebSocket URL & subscription pattern as useTransferQueueWebSocket.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const PING_INTERVAL_MS = 30_000;
const RECONNECT_BASE_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 20;
const NORMAL_CLOSE_CODE = 1000;

export interface PostedOrderWsEvent {
  action: 'created' | 'updated' | 'deleted';
  postedOrder: Record<string, any>;
  timestamp?: string;
}

export interface UsePostedOrderWebSocketOptions {
  userId?: string;
  jobLocations?: string[];
  onPostedOrderEvent?: (event: PostedOrderWsEvent) => void;
  enabled?: boolean;
}

export interface UsePostedOrderWebSocketReturn {
  isConnected: boolean;
  reconnectAttempts: number;
}

function getWsBaseUrl(): string {
  const env = import.meta.env.VITE_WS_URL;
  if (env?.trim()) return env.trim();
  return 'wss://xg615ecipc.execute-api.ca-central-1.amazonaws.com/dev/';
}

function buildWsUrl(userId?: string, jobLocations?: string[]): string {
  const base = getWsBaseUrl();
  const params = new URLSearchParams();
  if (userId) params.set('userId', userId);
  if (jobLocations?.length) params.set('jobLocations', jobLocations.join(','));
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

function isPostedOrderMessage(
  msg: unknown
): msg is { action: string; data: { postedOrder: Record<string, any>; action: string }; timestamp?: string } {
  if (typeof msg !== 'object' || msg === null) return false;
  const m = msg as Record<string, any>;
  return m.action === 'posted_order_update';
}

export function usePostedOrderWebSocket({
  userId,
  jobLocations,
  onPostedOrderEvent,
  enabled = true,
}: UsePostedOrderWebSocketOptions): UsePostedOrderWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<number | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const enabledRef = useRef(enabled);
  const onEventRef = useRef(onPostedOrderEvent);
  const jobLocationsRef = useRef(jobLocations);

  useEffect(() => { onEventRef.current = onPostedOrderEvent; }, [onPostedOrderEvent]);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => { jobLocationsRef.current = jobLocations; }, [jobLocations]);

  const clearPingInterval = useCallback(() => {
    if (pingIntervalRef.current !== null) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const sendSubscribe = useCallback((ws: WebSocket, locations: string[]) => {
    if (ws.readyState === WebSocket.OPEN && locations.length > 0) {
      ws.send(JSON.stringify({ type: 'subscribe', jobLocations: locations }));
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!enabledRef.current) return;
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) return;

    clearReconnectTimer();
    const attempt = reconnectAttemptsRef.current;
    const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS);
    reconnectAttemptsRef.current += 1;
    setReconnectAttempts(reconnectAttemptsRef.current);
    reconnectTimerRef.current = setTimeout(() => { connectRef.current(); }, delay);
  }, [clearReconnectTimer]);

  const connect = useCallback(() => {
    if (!enabledRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (wsRef.current?.readyState === WebSocket.CONNECTING) return;

    const url = buildWsUrl(userId, jobLocationsRef.current ?? undefined);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        setReconnectAttempts(0);

        const locations = jobLocationsRef.current;
        if (locations?.length) sendSubscribe(ws, locations);

        clearPingInterval();
        pingIntervalRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL_MS);
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          const msg = JSON.parse(event.data as string) as unknown;
          if (isPostedOrderMessage(msg)) {
            const payload = msg.data;
            const wsEvent: PostedOrderWsEvent = {
              action: (payload?.action as PostedOrderWsEvent['action']) ?? 'updated',
              postedOrder: payload?.postedOrder ?? payload ?? {},
              timestamp: msg.timestamp,
            };
            onEventRef.current?.(wsEvent);
          }
        } catch {
          // ignore non-JSON or irrelevant messages
        }
      };

      ws.onclose = (event: CloseEvent) => {
        setIsConnected(false);
        clearPingInterval();
        wsRef.current = null;
        if (enabledRef.current && event.code !== NORMAL_CLOSE_CODE) {
          scheduleReconnect();
        }
      };

      ws.onerror = () => {
        // reconnect handled in onclose
      };
    } catch {
      scheduleReconnect();
    }
  }, [userId, clearPingInterval, sendSubscribe, scheduleReconnect]);

  const connectRef = useRef(connect);
  connectRef.current = connect;

  useEffect(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && jobLocations?.length) {
      sendSubscribe(wsRef.current, jobLocations);
    }
  }, [jobLocations, sendSubscribe]);

  useEffect(() => {
    if (!enabled) {
      clearPingInterval();
      clearReconnectTimer();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close(NORMAL_CLOSE_CODE, 'Disabled');
        wsRef.current = null;
      }
      setIsConnected(false);
      return;
    }

    connect();

    return () => {
      clearPingInterval();
      clearReconnectTimer();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close(NORMAL_CLOSE_CODE, 'Unmount');
        wsRef.current = null;
      }
      setIsConnected(false);
    };
  }, [enabled, userId, connect, clearPingInterval, clearReconnectTimer]);

  return { isConnected, reconnectAttempts };
}
